import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { ActionRequiredBadge } from 'components/ActionRequiredBadge/ActionRequiredBadge';
import { ListTableLongCell } from 'components/ListTable/components/ListTableLongCell/ListTableLongCell';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { ProcurosOnlyBadge } from 'components/TaskSlideOver/components/ProcurosOnlyBadge';

export type TaskStatus = 'TODO' | 'DONE' | 'REJECTED' | 'IN_REVIEW';

export type Ownership = 'you' | 'trade_partner' | 'procuros';

export type ConnectionSetupTableRow = {
  id: string;
  status: TaskStatus;
  title: ReactNode;
  ownership: Ownership;
  actionRequired: boolean;
  path?: string;
};

type ConnectionSetupTableProps = {
  rows: Array<ConnectionSetupTableRow>;
};

const TASK_COLUMN = 'w-2/3 max-w-0';

export const ConnectionSetupTable = ({ rows }: ConnectionSetupTableProps) => {
  const { t } = useTranslation();

  const headers: Array<TableHeader> = [
    { label: t('connections:relationshipDetails.setupTable.headers.tasks'), key: 'tasks' },
    { label: t('connections:relationshipDetails.setupTable.headers.ownership'), key: 'ownership' },
    { label: '', key: 'actionRequired', invisible: true },
  ];

  const rowBuilder = (row: ConnectionSetupTableRow): Array<TableItem> => [
    {
      key: `tasks-${row.id}`,
      className: TASK_COLUMN,
      element: <TaskTitleCell title={row.title} status={row.status} />,
    },
    {
      key: `ownership-${row.id}`,
      element: <span className="text-xs text-gray-700">{t(`common:ownership.${row.ownership}`)}</span>,
    },
    {
      key: `actionRequired-${row.id}`,
      element: !row.actionRequired ? null : row.ownership === 'procuros' ? (
        <ProcurosOnlyBadge label={t('common:internalActionRequired')} />
      ) : (
        <ActionRequiredBadge />
      ),
    },
  ];

  return (
    <ListTable<ConnectionSetupTableRow>
      headers={headers}
      data={rows}
      isLoading={false}
      rowBuilder={rowBuilder}
      rowClickPath={(row) => row.path}
    />
  );
};

const TaskTitleCell = ({ title, status }: { title: ReactNode; status: TaskStatus }) => (
  <div className="flex min-w-0 items-center gap-3">
    <TaskStatusIcon status={status} />
    <ListTableLongCell tooltipContent={title} maxWidthClassName="max-w-full">
      <span className="text-xs text-gray-700">{title}</span>
    </ListTableLongCell>
  </div>
);

const TaskStatusIcon = ({ status }: { status: TaskStatus }) => {
  switch (status) {
    case 'DONE':
      return <CheckCircleIcon className="size-5 text-green-500" />;
    case 'REJECTED':
      return <ExclamationCircleIcon className="size-5 text-red-600" />;
    case 'IN_REVIEW':
      return <ClockIcon className="size-5 text-yellow-500" />;
    case 'TODO':
      return (
        <div className="flex size-5 items-center justify-center">
          <div className="flex size-4 items-center justify-center rounded-full bg-gray-100">
            <svg className="size-2 text-gray-400" fill="currentColor" viewBox="0 0 8 8">
              <circle cx={4} cy={4} r={3} />
            </svg>
          </div>
        </div>
      );
  }
};
